'use client'

import { cn } from '@brew/ui/cn'
import { ArrowUpRight, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { IntegrationBrandLogo } from '@/app/(app)/integrations/components/integration-brand-logo'
import { useAppLocale } from '@/components/i18n/locale-provider'
import {
  MK_BODY,
  MK_CAPTION,
  MK_CARD_TITLE,
} from '@/components/marketing/home/home-layout'
import {
  TILE,
  TileCopy,
  TilePanel,
} from '@/components/marketing/home/tile-panel'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { localizePath } from '@/lib/i18n/routing'
import {
  type IntegrationMeta,
  integrationData,
  integrationTypeLabel,
} from '@/lib/integrations/catalog'

type Direction = IntegrationMeta['dataDirection']

/** Each integration's one line, in the reader's language, by its id. */
type ShortDescriptions = Readonly<Record<string, string>>

/** `marketingShared.integrationDirectory`: its sections and direction labels. */
const DIRECTION_KEY = {
  data_in: 'dataIn',
  data_out: 'dataOut',
} as const satisfies Record<Direction, string>

/** Destinations first: what Brew sends out, then what it pulls in. */
const SECTION_ORDER: ReadonlyArray<Direction> = ['data_out', 'data_in']

const LINK_TILE = cn(
  TILE,
  'transition-colors duration-150 hover:bg-mk-card focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:-outline-offset-2'
)

function TileBody({
  integration,
  shortDescription,
}: {
  integration: IntegrationMeta
  shortDescription: string
}) {
  const t = useTranslations('marketingShared.integrationDirectory')
  // How it connects, worded like the app's integration cards; the browse
  // layout provides the catalog.
  const tIntegrations = useTranslations('integrations')
  const isComingSoon = integration.availability === 'coming_soon'
  return (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center overflow-clip rounded-[10px] bg-white shadow-[0_0_0_1px_var(--mk-ring)]">
        <IntegrationBrandLogo
          variant="card"
          brandDomain={integration.brandDomain}
          provider={integration.id}
          name={integration.name}
          tone={integration.logoTone}
          background={integration.logoBackground}
          className="size-full"
          imgClassName="size-full object-cover"
        />
      </span>
      <TileCopy title={integration.name} className="pr-6">
        {shortDescription}
      </TileCopy>
      <span
        className={cn(
          MK_CAPTION,
          'mt-auto flex items-center justify-between gap-2 text-mk-body'
        )}
      >
        <span>
          {integrationTypeLabel(tIntegrations, integration.integrationType)} ·{' '}
          {t(`directions.${DIRECTION_KEY[integration.dataDirection]}`)}
        </span>
        {isComingSoon ? (
          <span className="font-medium text-mk-accent">{t('comingSoon')}</span>
        ) : null}
      </span>
      {isComingSoon ? null : (
        <ArrowUpRight
          aria-hidden="true"
          className="absolute top-5 right-5 size-4 text-mk-ink/75"
        />
      )}
    </>
  )
}

/** One integration: a link to its page, or a muted tile while it is coming soon. */
function IntegrationDirectoryTile({
  integration,
  shortDescription,
}: {
  integration: IntegrationMeta
  shortDescription: string
}) {
  const locale = useAppLocale()
  if (integration.availability === 'coming_soon') {
    return (
      <div className={cn(TILE, 'opacity-60')}>
        <TileBody
          integration={integration}
          shortDescription={shortDescription}
        />
      </div>
    )
  }
  return (
    <IntentPrefetchLink
      href={localizePath(locale, `/browse/integrations/${integration.id}`)}
      className={LINK_TILE}
    >
      <TileBody integration={integration} shortDescription={shortDescription} />
    </IntentPrefetchLink>
  )
}

/**
 * The public integrations directory in the home page's language: a search
 * field, then the catalog split into destinations and sources, each a
 * `TilePanel` of linked tiles (logo, name, one line, how it connects).
 * Coming-soon integrations stay visible but never link. Rendered by
 * `IntegrationDirectory`, which gives it the `marketingShared` catalog and
 * each integration's one line (`shortDescriptions`).
 */
export function SearchableIntegrationDirectory({
  shortDescriptions,
}: {
  shortDescriptions: ShortDescriptions
}) {
  const t = useTranslations('marketingShared.integrationDirectory')
  const [query, setQuery] = useState('')

  const sections = useMemo(() => {
    const needle = query.toLowerCase().trim()
    const matches = needle
      ? integrationData.filter(
          (integration) =>
            integration.name.toLowerCase().includes(needle) ||
            (shortDescriptions[integration.id] ?? '')
              .toLowerCase()
              .includes(needle)
        )
      : integrationData
    return SECTION_ORDER.map((id) => ({
      id,
      items: matches.filter((integration) => integration.dataDirection === id),
    })).filter((section) => section.items.length > 0)
  }, [query, shortDescriptions])

  return (
    <div className="flex w-full flex-col gap-12">
      <label className="relative mx-auto w-full max-w-xl">
        <span className="sr-only">{t('search')}</span>
        <Search
          aria-hidden="true"
          className="absolute top-1/2 left-5 size-4 -translate-y-1/2 text-mk-body"
        />
        <input
          type="search"
          name="integration-search"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('search')}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          className={cn(
            MK_BODY,
            'h-12 w-full rounded-full border border-mk-line bg-mk-tile pr-5 pl-11 text-mk-ink outline-none transition-colors placeholder:text-mk-body focus-visible:border-mk-ink/30'
          )}
        />
      </label>

      {sections.length === 0 ? (
        <p className={cn(MK_BODY, 'py-12 text-center text-mk-body')}>
          {t('noMatch', { query })}
        </p>
      ) : (
        sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`integrations-${section.id}`}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-1.5">
              <h2
                id={`integrations-${section.id}`}
                className={cn(MK_CARD_TITLE, 'text-mk-ink')}
              >
                {t(`sections.${DIRECTION_KEY[section.id]}.title`)}
              </h2>
              <p className={cn(MK_BODY, 'text-mk-body')}>
                {t(`sections.${DIRECTION_KEY[section.id]}.description`)}
              </p>
            </div>
            <TilePanel columns={3}>
              {section.items.map((integration) => (
                <IntegrationDirectoryTile
                  key={integration.id}
                  integration={integration}
                  shortDescription={shortDescriptions[integration.id] ?? ''}
                />
              ))}
            </TilePanel>
          </section>
        ))
      )}
    </div>
  )
}
